import { HttpRequest, HttpResponse } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { nth } from "lodash";
import moment from "moment";
import { Observable } from "rxjs";
import { v4 as uuid } from "uuid";
import { CreateAsmtService } from "../offline-asmt-data-service/create-asmt-service";
import { OfflineAsmtDataServiceProvider } from "../offline-asmt-data-service/offline-asmt-data-service";
import {
  DB_DATE_FORMAT,
  OFFLINE_ACTION_VALIDITY_IN_DAYS,
  OFFLINE_APIS,
  OFFLINE_API_PATTERNS,
  STATIC_API_PATTERNS,
} from "../offline-manager/offline-globals";
import { PatientDataManager } from "../offline-manager/patient-data-manager";

@Injectable()
export class OfflineRequestProcessor {
  constructor(
    private asmtDataManager: OfflineAsmtDataServiceProvider,
    private patientDataManager: PatientDataManager,
    private createAsmtService: CreateAsmtService
  ) {}

  handleRequestOffline(request: HttpRequest<any>): Observable<any> {
    return new Observable((observer) => {
      this.handleRequest(request).subscribe(
        (response) => {
          observer.next(new HttpResponse<any>({ body: response, status: 200 }));
          observer.complete();
        },
        (error) => {
          // Find better way to handle http errors
          if (error.errorMessage && error.errorMessage.key) {
            observer.error(error);
          }
          observer.next(new HttpResponse<any>({ body: error, status: 400 }));
          observer.complete();
        }
      );
    });
  }

  handleRequest(request: HttpRequest<any>): Observable<any> {
    // Only process valid urls
    if (!this.isValidUrl(request.url)) {
      return Observable.empty();
    }

    //Get API info and return Data from SQlite
    let api = this.checkOfflineDataAPI(request);

    if (request.method === "PUT") {
      if (api === OFFLINE_APIS.assessmentSectionData) {
        const offlineAction = this.prepareSectionOfflineActionObj(request);
        return this.asmtDataManager.offlineEditAssessment(offlineAction);
      } else if (api === OFFLINE_APIS.assessmentSectionCardEntry) {
        const offlineAction = this.prepareCardDetailOfflineActionObj(request);
        return this.asmtDataManager.offlineEditAssessment(offlineAction);
      }
    }

    if (request.method === "POST") {
      if (api === OFFLINE_APIS.assessmentSectionCard) {
        const offlineAction = this.prepareCardOfflineActionObj(request);
        return this.asmtDataManager.offlineEditAssessment(offlineAction);
      }
    }

    if (request.method === "DELETE") {
      if (api === OFFLINE_APIS.assessmentSectionCardEntry) {
        const offlineAction = this.prepareCardDetailOfflineActionObj(request);
        return this.asmtDataManager.offlineEditAssessment(offlineAction);
      }
    }

    if (this.isStaticAPI(api)) {
      return this.asmtDataManager.getStaticData(api);
    } else if (api === OFFLINE_APIS.visits) {
      let PatientId = this.getInLineParamFromURL(
        request.url,
        "patient-charts/",
        "/visits"
      );
      return this.asmtDataManager.getAssessments(Number(PatientId));
    } else if (api === OFFLINE_APIS.profile) {
      let PatientId = this.getInLineParamFromURL(
        request.url,
        "patients/",
        "/profiles"
      );
      return this.patientDataManager.getPatientProfile(Number(PatientId));
    } else if (api === OFFLINE_APIS.visitFrequencies) {
      return Observable.of({
        visitFrequencies: [],
        x_total_count: 0,
      });
    } else if (api === OFFLINE_APIS.assessmentType) {
      const patientChartId = parseInt(
        this.getInLineParamFromURL(
          request.url,
          "patient-charts/",
          "/visit-types"
        )
      );
      const asmtRolesAndTypes = { assessment_roles: [] };
      return this.asmtDataManager.createAssessmentTypeResponse(
        patientChartId,
        asmtRolesAndTypes
      );
    } else if (api === OFFLINE_APIS.clinicalOrderItems) {
      return Observable.of({
        content: [],
      });
    } else if (
      api === OFFLINE_APIS.assessmentNavigation ||
      api === OFFLINE_APIS.assessmentSectionTemplate
    ) {
      let asmtId = this.getInLineParamFromURL(
        request.url,
        "visits/",
        "/template"
      );
      let section = request.url.split("/").pop();
      return this.asmtDataManager.getAssessmentTemplate(
        Number(asmtId),
        section
      );
    } else if (api === OFFLINE_APIS.patientInfoFromQBOSId) {
      const qbosId = request.url.split("/").pop();
      return this.asmtDataManager.getDareStatus(qbosId);
    } else if (api === OFFLINE_APIS.assessmentDetails) {
      const asmtId = request.url.split("/").pop();
      return this.asmtDataManager.getAssessmentDetails(Number(asmtId));
    } else if (api === OFFLINE_APIS.assessmentSectionData) {
      const sectionUrl = request.url.split("/");
      const sectionId = nth(sectionUrl, -1);
      const asmtId = nth(sectionUrl, -3);
      return this.asmtDataManager.getAssessmentDetailsBySection(
        Number(asmtId),
        sectionId
      );
    } else if (api === OFFLINE_APIS.assessment) {
      if (request.method === "POST") {
        const payload = request.body;
        return this.createAsmtService.createVisit(payload);
      }
    }

    return Observable.empty();
  }

  isStaticAPI(api: string): boolean {
    let isStatic: boolean = false;
    for (var i = 0; i < STATIC_API_PATTERNS.length; i++) {
      if (STATIC_API_PATTERNS[i].apiPath === api) {
        isStatic = true;
        break;
      }
    }
    return isStatic;
  }

  checkOfflineDataAPI(request: HttpRequest<any>): string {
    let apiPath;

    for (var i = 0; i < OFFLINE_API_PATTERNS.length; i++) {
      let apiPattern = OFFLINE_API_PATTERNS[i];
      if (apiPattern.pattern.test(request.url)) {
        apiPath = apiPattern.apiPath;
        break;
      }
    }

    return apiPath;
  }

  getPathName(url: string): string {
    let path = new URL(url);
    return path.pathname;
  }

  isValidUrl(url: string): boolean {
    try {
      new URL(url);
      return true;
    } catch (_) {
      return false;
    }
  }

  prepareSectionOfflineActionObj(request: HttpRequest<any>): any {
    const params = request.url.split("/");
    const sectionId = nth(params, -1);
    const asmtId = nth(params, -3);
    const modifiedData = request.body;

    return this.prepareOfflineActionObj(modifiedData, asmtId, sectionId);
  }

  prepareCardOfflineActionObj(request: HttpRequest<any>): any {
    const params = request.url.split("/");
    const cardId = nth(params, -1);
    const sectionId = nth(params, -3);
    const asmtId = nth(params, -5);
    const modifiedData = request.body;
    const cardDetails = {
      cardId,
      cardAction: request.method,
    };
    return this.prepareOfflineActionObj(
      modifiedData,
      asmtId,
      sectionId,
      cardDetails
    );
  }

  prepareCardDetailOfflineActionObj(request: HttpRequest<any>): any {
    const params = request.url.split("/");
    const cardDetailsUUID = nth(params, -1);
    const cardId = nth(params, -2);
    const sectionId = nth(params, -4);
    const asmtId = nth(params, -6);
    const modifiedData = request.body;
    const cardDetails = {
      cardId,
      cardAction: request.method,
      cardDetailsUUID,
    };
    return this.prepareOfflineActionObj(
      modifiedData,
      asmtId,
      sectionId,
      cardDetails
    );
  }

  prepareOfflineActionObj(
    modifiedData: any,
    asmtId,
    sectionId: string,
    cardDetails: any = {}
  ): any {
    const updated_at = moment();
    const expire_at = moment()
      .add(OFFLINE_ACTION_VALIDITY_IN_DAYS, "days")
      .startOf("day");

    let cardListUUID = cardDetails.cardDetailsUUID || null;
    const modifiedDataDbJSON = modifiedData ? { ...modifiedData } : {};
    modifiedDataDbJSON.id = cardListUUID ? cardListUUID : uuid();

    return {
      assessment_id: asmtId,
      action: "UPDATE",
      modifiedData,
      modifiedDataDbJSON: modifiedDataDbJSON,
      visit_type: null,
      patient_id: null,
      patient_name: null,
      updated_at: updated_at,
      updateAtStr: updated_at.format(DB_DATE_FORMAT),
      expire_at: expire_at.valueOf(),
      sectionId,
      cardId: cardDetails.cardId || null,
      cardDetailsUUID: cardListUUID,
      cardAction: cardDetails.cardAction || null,
    };
  }

  getInLineParamFromURL(
    url: string,
    leftToken: string,
    rightToken: string
  ): string {
    let param: string;
    try {
      param = url.substring(
        url.indexOf(leftToken) + leftToken.length,
        url.indexOf(rightToken)
      );
    } catch (e) {
      console.log("getInLineParamFromURL error");
    }
    return param;
  }
}



// WEBPACK FOOTER //
// ./src/providers/offline-manager/offline-request-processor.ts